/**
 * QuestionsPage - Dedicated Questions for My Doctor Management
 * 
 * Features:
 * - Unassigned questions (not attached to appointment)
 * - Upcoming appointment questions
 * - Answered questions
 * - Add to appointment functionality
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ArrowLeft, 
  Plus, 
  MessageSquareText, 
  Calendar,
  Edit3,
  Trash2,
  CheckCircle2,
  CalendarPlus,
  Loader2,
  HelpCircle,
  MessageCircle
} from 'lucide-react';
import { useDemo } from '@/contexts/DemoContext';
import API from '@/utils/api';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const THEME = {
  bgGradient: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 30%, #E0E7FF 60%, #F0FDF4 100%)',
};

const QuestionsPage = () => {
  const navigate = useNavigate();
  const { isDemoMode, demoData } = useDemo();
  
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [activeTab, setActiveTab] = useState('unanswered');
  const [appointments, setAppointments] = useState([]);
  const [showAddToApptDialog, setShowAddToApptDialog] = useState(null);
  const [showAnswerDialog, setShowAnswerDialog] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    question: '',
    context: '',
    priority: 'normal',
  });
  
  const [answerText, setAnswerText] = useState('');

  useEffect(() => {
    fetchQuestions();
    fetchAppointments();
  }, [isDemoMode]);

  const fetchQuestions = async () => {
    if (isDemoMode) {
      setQuestions(demoData?.medical?.questions || []);
      setLoading(false);
      return;
    }
    
    try {
      const response = await API.get('/medical/questions');
      setQuestions(response.data || []);
    } catch (error) {
      console.error('Failed to fetch questions:', error);
      toast.error('Failed to load questions');
    } finally {
      setLoading(false);
    }
  };

  const fetchAppointments = async () => {
    if (isDemoMode) return;
    
    try {
      const response = await API.get('/medical/appointment-preps');
      setAppointments(response.data?.filter(a => a.status !== 'completed') || []);
    } catch (error) {
      console.error('Failed to fetch appointments:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      question: '',
      context: '',
      priority: 'normal',
    });
    setEditingQuestion(null);
  };

  const handleOpenAdd = () => {
    resetForm();
    setShowAddDialog(true);
  };

  const handleOpenEdit = (question) => {
    setFormData({
      question: question.question || question.text || '',
      context: question.context || question.notes || '',
      priority: question.priority || 'normal',
    });
    setEditingQuestion(question);
    setShowAddDialog(true);
  };

  const handleSave = async () => {
    if (!formData.question.trim()) {
      toast.error('Please enter a question');
      return;
    }

    if (isDemoMode) {
      toast.success(editingQuestion ? 'Question updated (demo)' : 'Question added (demo)');
      setShowAddDialog(false);
      resetForm();
      return;
    }

    setSaving(true);
    try {
      const dataToSave = {
        question: formData.question,
        text: formData.question, // Alias for compatibility
        context: formData.context,
        notes: formData.context, // Alias
        priority: formData.priority,
        answered: editingQuestion?.answered || false,
        answer: editingQuestion?.answer || null,
      };

      if (editingQuestion) {
        await API.put(`/medical/questions/${editingQuestion.id}`, dataToSave);
        toast.success('Question updated');
      } else {
        await API.post('/medical/questions', dataToSave);
        toast.success('Question added');
      }
      
      fetchQuestions();
      setShowAddDialog(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save question');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (question) => {
    if (isDemoMode) {
      toast.success('Question deleted (demo)');
      setDeleteConfirm(null);
      return;
    }

    try {
      await API.delete(`/medical/questions/${question.id}`);
      toast.success('Question deleted');
      fetchQuestions();
    } catch (error) {
      toast.error('Failed to delete question');
    }
    setDeleteConfirm(null);
  };

  const handleMarkAnswered = async (question, answer) => {
    if (isDemoMode) {
      toast.success('Question marked answered (demo)');
      setShowAnswerDialog(null);
      return;
    }

    try {
      await API.put(`/medical/questions/${question.id}`, {
        ...question,
        answered: true,
        answer: answer,
        answered_date: format(new Date(), 'yyyy-MM-dd'),
      });
      toast.success('Question marked as answered');
      fetchQuestions();
      setShowAnswerDialog(null);
      setAnswerText('');
    } catch (error) {
      toast.error('Failed to update question');
    }
  };

  const handleAddToAppointment = async (question, appointmentId) => {
    if (isDemoMode) {
      toast.success('Added to appointment (demo)');
      setShowAddToApptDialog(null);
      return;
    }

    try {
      // Get current appointment
      const apptRes = await API.get(`/medical/appointment-preps/${appointmentId}`);
      const appointment = apptRes.data;
      
      // Add question to appointment
      const updatedQuestions = [...(appointment.questions || []), {
        id: question.id,
        text: question.question || question.text,
        answered: false,
        fromQuestionLog: true,
      }];
      
      await API.put(`/medical/appointment-preps/${appointmentId}`, {
        questions: updatedQuestions,
      });
      
      toast.success('Added to appointment');
      setShowAddToApptDialog(null);
    } catch (error) {
      toast.error('Failed to add to appointment');
    }
  };

  // Filter questions
  const unansweredQuestions = questions.filter(q => !q.answered);
  const answeredQuestions = questions.filter(q => q.answered);

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'high':
        return <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">Important</Badge>;
      case 'low':
        return <Badge variant="outline" className="text-xs bg-slate-50 text-slate-600 border-slate-200">Low priority</Badge>;
      default:
        return null;
    }
  };

  const renderQuestionCard = (question) => (
    <Card key={question.id} className="mb-3" data-testid={`question-${question.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
            question.answered ? 'bg-green-100' : 'bg-blue-100'
          }`}>
            {question.answered ? (
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            ) : (
              <HelpCircle className="h-5 w-5 text-blue-600" />
            )}
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h3 className="font-medium text-slate-800">{question.question || question.text}</h3>
              {getPriorityBadge(question.priority)}
              {question.answered && (
                <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                  Answered
                </Badge>
              )}
            </div>
            
            {(question.context || question.notes) && (
              <p className="text-sm text-slate-500 mt-1">{question.context || question.notes}</p>
            )}
            
            {question.answered && question.answer && (
              <div className="mt-3 p-3 bg-green-50 rounded-lg">
                <div className="text-xs font-medium text-green-800 mb-1">Doctor&apos;s Answer:</div>
                <p className="text-sm text-green-700">{question.answer}</p>
              </div>
            )}
            
            {/* Action buttons */}
            <div className="flex flex-wrap gap-2 mt-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleOpenEdit(question)}
                className="text-xs h-7"
              >
                <Edit3 className="h-3 w-3 mr-1" />
                Edit
              </Button>
              
              {!question.answered && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowAnswerDialog(question);
                      setAnswerText('');
                    }}
                    className="text-xs h-7 text-green-600 hover:text-green-700"
                  >
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Mark Answered
                  </Button>
                  
                  {appointments.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowAddToApptDialog(question)}
                      className="text-xs h-7 text-blue-600 hover:text-blue-700"
                    >
                      <CalendarPlus className="h-3 w-3 mr-1" />
                      Add to Appointment
                    </Button>
                  )}
                </>
              )}
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDeleteConfirm(question)}
                className="text-xs h-7 text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="h-3 w-3 mr-1" />
                Delete
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: THEME.bgGradient }}>
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen pb-28"
      style={{ background: THEME.bgGradient }}
      data-testid="questions-page"
    >
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-blue-100">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/medical')}
              className="p-2 -ml-2"
            >
              <ArrowLeft className="h-5 w-5 text-slate-600" />
            </Button>
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <MessageSquareText className="h-5 w-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <h1 className="text-lg font-semibold text-slate-800">Questions for My Doctor</h1>
              <p className="text-xs text-slate-500">Don&apos;t forget to ask</p>
            </div>
            <Button
              size="sm"
              onClick={handleOpenAdd}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="unanswered" className="text-sm">
              To Ask ({unansweredQuestions.length})
            </TabsTrigger>
            <TabsTrigger value="answered" className="text-sm">
              Answered ({answeredQuestions.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="unanswered">
            {unansweredQuestions.length === 0 ? (
              <div className="text-center py-12 bg-white/50 rounded-lg">
                <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="font-medium text-slate-800 mb-2">No questions yet</h3>
                <p className="text-sm text-slate-500 mb-4">Write down questions before your visit</p>
                <Button onClick={handleOpenAdd} className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Question
                </Button>
              </div>
            ) : (
              unansweredQuestions.map(renderQuestionCard)
            )}
          </TabsContent>

          <TabsContent value="answered">
            {answeredQuestions.length === 0 ? (
              <div className="text-center py-12 bg-white/50 rounded-lg">
                <p className="text-sm text-slate-500">No answered questions yet</p>
              </div>
            ) : (
              answeredQuestions.map(renderQuestionCard)
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(open) => !open && setShowAddDialog(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingQuestion ? 'Edit Question' : 'Add Question'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="question">Question *</Label>
              <Textarea
                id="question"
                value={formData.question}
                onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                placeholder="What do you want to ask your doctor?"
                rows={3}
              />
            </div>

            <div>
              <Label htmlFor="context">Context or Notes</Label>
              <Textarea
                id="context"
                value={formData.context}
                onChange={(e) => setFormData({ ...formData, context: e.target.value })}
                placeholder="Any background information"
                rows={2}
              />
            </div>

            <div>
              <Label>Priority</Label>
              <div className="flex gap-2 mt-2">
                {['low', 'normal', 'high'].map(p => (
                  <Button
                    key={p}
                    type="button"
                    variant={formData.priority === p ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFormData({ ...formData, priority: p })}
                    className={formData.priority === p ? 'bg-blue-600' : ''}
                  >
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingQuestion ? 'Update' : 'Add'} Question
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Question</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this question? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleDelete(deleteConfirm)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add to Appointment Dialog */}
      <Dialog open={!!showAddToApptDialog} onOpenChange={() => setShowAddToApptDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add to Appointment</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-4">
              Select an appointment to add this question to:
            </p>
            <div className="space-y-2">
              {appointments.map(appt => (
                <Card 
                  key={appt.id}
                  className="cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => handleAddToAppointment(showAddToApptDialog, appt.id)}
                >
                  <CardContent className="p-3">
                    <div className="font-medium text-sm text-slate-800">
                      {appt.doctorName || 'Appointment'}
                    </div>
                    <div className="text-xs text-slate-500">
                      {appt.appointmentDate && format(parseISO(appt.appointmentDate), 'MMM d, yyyy')}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddToApptDialog(null)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark Answered Dialog */}
      <Dialog open={!!showAnswerDialog} onOpenChange={() => setShowAnswerDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record Answer</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="p-3 bg-blue-50 rounded-lg mb-4">
              <p className="text-sm font-medium text-blue-800">{showAnswerDialog?.question || showAnswerDialog?.text}</p>
            </div>
            <Label htmlFor="answer">Doctor&apos;s Answer (optional)</Label>
            <Textarea
              id="answer"
              value={answerText}
              onChange={(e) => setAnswerText(e.target.value)}
              placeholder="What did the doctor say?"
              rows={4}
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAnswerDialog(null)}>Cancel</Button>
            <Button 
              onClick={() => handleMarkAnswered(showAnswerDialog, answerText)}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Mark Answered
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuestionsPage;
